import React, { useState } from 'react';
import './FAQs.css';

interface FAQItem {
  question: string;
  answer: React.ReactNode;
}

const faqItems: FAQItem[] = [
  {
    question: "What is NatureDr?",
    answer: (
      <>
        <p>
          NatureDr is your friendly holistic health companion, blending ancient wisdom with modern wellness approaches. We provide natural, herb-based, and eco-friendly remedies for your everyday health concerns.
        </p>
        <p>
          All remedies suggested by NatureDr are supported by extensive research in Ayurvedic health practices, Traditional Chinese Medicine, and holistic wellness philosophies that have stood the test of time.
        </p>
      </>
    )
  },
  {
    question: "Why choose natural remedies with NatureDr?",
    answer: (
      <>
        <p>
          Imagine addressing your health concerns naturally, without immediately turning to synthetic medications or scheduling doctor visits for minor symptoms.
        </p>
        <p>
          NatureDr empowers you with knowledge of time-tested natural alternatives that can be gentler on your body while still being effective. It's about expanding your wellness toolkit with options that complement modern medicine.
        </p>
      </>
    )
  },
  {
    question: "What if the suggestions don't work for me?",
    answer: (
      <p>
        Your health and safety are our top priorities. While many find relief with natural approaches, everyone's body responds differently. If your symptoms persist or worsen after trying our suggestions, we strongly recommend consulting with a qualified healthcare professional. NatureDr is designed to complement, not replace, professional medical care.
      </p>
    )
  },
  {
    question: "Is NatureDr free to use?",
    answer: (
      <>
        <p>
          We believe in making holistic health knowledge accessible to everyone! NatureDr offers a generous 7-day free trial with full access to all features.
        </p>
        <p>
          After your trial, you can continue your wellness journey with our affordable subscription plans. Check out our <a href="/pricing" className="faq-link">Pricing</a> page for current rates and special offers.
        </p>
      </>
    )
  },
  {
    question: "Are natural remedies scientifically proven?",
    answer: (
      <p>
        Many natural remedies have centuries of historical use and growing scientific backing. NatureDr focuses on sharing remedies with both traditional wisdom and emerging research support. We clearly indicate when suggestions are based primarily on traditional use versus those with stronger scientific evidence, empowering you to make informed decisions about your wellness journey.
      </p>
    )
  },
  {
    question: "Have suggestions for improving NatureDr?",
    answer: (
      <>
        <p>
          We're constantly growing and evolving! Your feedback helps make NatureDr better for everyone in our wellness community.
        </p>
        <p>
          Have an idea or suggestion? We'd love to hear it! Submit your thoughts through our feedback form below.
        </p>
        <div className="feedback-placeholder">
          <p className="feedback-note">Feature request submission coming soon!</p>
        </div>
      </>
    )
  },
];

const FAQs: React.FC = () => {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const toggleFAQ = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <div className="faqs-container">
      <div className="faqs-header">
        <div className="faqs-header-icon">
          <i className="fa-solid fa-seedling" />
        </div>
        <h1>Frequently Asked Questions</h1>
        <p className="faqs-subtitle">Discover how NatureDr can support your natural wellness journey</p>
      </div>

      <div className="faqs-list">
        {faqItems.map((item, index) => (
          <div
            key={index}
            className={`faq-item ${openIndex === index ? 'active' : ''}`}
            style={{ animationDelay: `${index * 0.08}s` }}
          >
            <button
              className="faq-question"
              onClick={() => toggleFAQ(index)}
              aria-expanded={openIndex === index}
            >
              <span className="faq-question-text">{item.question}</span>
              <span className="faq-chevron">
                <i className="fa-solid fa-chevron-down" />
              </span>
            </button>
            <div className={`faq-answer ${openIndex === index ? 'open' : ''}`}>
              <div className="faq-answer-content">
                {item.answer}
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="faqs-footer">
        <p>Still have questions? <a href="mailto:support@naturedr.com" className="contact-link">Contact our support team</a></p>
      </div>
    </div>
  );
};

export default FAQs;
